<template>
    <div id="location-map" class="vrm--map__location">
        <div id="search-box-container" class="search-box-container"></div>

        <vrm-mapbox
            :location="initialLocation"
            fullscreen-target="#location-map"
            @load="onMapboxLoaded"
        ></vrm-mapbox>
    </div>
</template>
<script>
import mapboxgl from 'mapbox-gl';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmMapbox from '@/vue/components/map/VrmMapbox.vue';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { MapboxSearchBox } from '@mapbox/search-js-web';
import { vrmConfig } from '@/config';
import { mapLayerIndividualInstallation } from '@/vue/components/map/constants';

@Component({
    computed: {
        vrmConfig() {
            return vrmConfig;
        },
    },
    components: { VrmMapbox },
    emits: ['location-changed'],
})
@AngularDirective('vrm-map-location-settings')
@AngularLocaleBundle('core')
export default class LocationSettingsMap extends Vue {
    @ngInject()
    VrmLangService;

    /**
     * Coordinates of the installation if they exist.
     *
     * @type { latitude, longitude }
     */
    @Prop({ type: Object })
    coords;

    /**
     * Center coords for the map
     *
     * @type { latitude, longitude }
     */
    @Prop({ type: Object })
    center;

    /**
     * Initial zoom level for the map
     */
    @Prop()
    zoom;

    /**
     * Language of the user.
     *
     * Used to set the search language.
     */
    language = null;

    /**
     * Map canvas object used to update cursor style when dragging.
     */
    canvas = null;

    geojson = {
        features: [],
    };

    isLocationInitialized = false;

    initialLocation = {
        lat: 0,
        lng: 0,
        zoom: 15,
        bearing: 0,
        pitch: 0,
    };

    clickTimeout = 200; // Click delay for moving the marker, in ms.
    clickTimeoutId;

    async created() {
        this.language = await this.VrmLangService.getLang();
    }

    /**
     * Called whenever the component gets a new 'center' value from the controller.
     *
     * The controller needs to fetch installation data before being able to pass the center coords. This can take some
     * time in which the map has no clue what to render yet. Map is initialized to 0,0 and moved as soon as it gets the
     * center coords from the controller for the first time.
     *
     * @param newValue
     * @param oldValue
     */
    @Watch('center', { deep: true })
    // eslint-disable-next-line no-unused-vars
    onCenterChanged(newValue, oldValue) {
        if (!this.isLocationInitialized) {
            let zoom = this.zoom || 15;
            if (this.map) {
                zoom = this.map.getZoom();
            }

            this.initialLocation = {
                lat: newValue.latitude || 0,
                lng: newValue.longitude || 0,
                zoom: zoom,
                bearing: 0,
                pitch: 0,
            };

            if (this.map) {
                this.map.flyTo([newValue.longitude, newValue.latitude]);
            }

            this.isLocationInitialized = true;
        }
    }

    /**
     * Called when the map gets a new 'coords' value from the controller.
     *
     * Updates the GeoJSON installation feature to the new location.
     *
     * @param newValue
     * @param oldValue
     */
    @Watch('coords', { deep: true })
    // eslint-disable-next-line no-unused-vars
    onCoordsChanged(newValue, oldValue) {
        if (this.geojson.features.length > 0) {
            // If there is already an existing feature, update its location.
            this.geojson.features[0].geometry.coordinates = [newValue.longitude, newValue.latitude];
            this.map.getSource('installation').setData(this.geojson);
        } else if (this.map) {
            // If no feature exists yet but there is a map, create the GeoJSON data.
            this.geojson = {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [newValue.longitude, newValue.latitude],
                        },
                    },
                ],
            };

            this.map.getSource('installation').setData(this.geojson);
        }
    }

    /**
     * If the language promise completes after the map has initialized, update the search box language.
     *
     * @param newVal
     */
    @Watch('language')
    onLanguageChanged(newVal) {
        if (this.searchBox) {
            this.searchBox.options.language = newVal;
        }
    }

    /**
     * Called whenever the Mapbox component is done setting up the map.
     *
     * Used to configure and set up the mapbox behavior.
     *
     * @param map
     */
    onMapboxLoaded(map) {
        this.map = map;

        this.canvas = map.getCanvasContainer();

        if (this.coords) {
            // Only create GeoJSON data if we already have the coordinates. If not, the data is created as soon as the
            // coords are updated and the watcher is run.
            this.geojson = {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [this.coords.longitude, this.coords.latitude],
                        },
                    },
                ],
            };
        }

        map.addSource('installation', {
            type: 'geojson',
            data: this.geojson,
        });

        map.addLayer(mapLayerIndividualInstallation);

        this.initMovableMarker();
        this.initSearchBox();
    }

    /**
     * Set up and initialize the Mapbox SearchBox and add it to the screen.
     */
    initSearchBox() {
        const searchBox = new MapboxSearchBox();

        searchBox.accessToken = vrmConfig.mapboxApiKey;
        searchBox.options = {
            language: this.language || 'en',
            types: 'country,place,city,address',
        };
        searchBox.mapboxgl = mapboxgl;
        searchBox.marker = false;
        searchBox.bindMap(this.map);

        document.getElementById('search-box-container').appendChild(searchBox);
        searchBox.addEventListener('retrieve', this.onRetrieve);

        this.searchBox = searchBox;
    }

    /**
     * Called when a Mapbox SearchBox suggestion is selected.
     *
     * Moves the map and installation marker to the newly selected location and emits an update event.
     *
     * @param e - Mapbox Retrieve event
     */
    onRetrieve(e) {
        const coords = e.detail.features[0].geometry.coordinates;

        this.map.jumpTo({
            center: coords,
            essential: true,
        });

        this.geojson.features[0].geometry.coordinates = coords;
        this.map.getSource('installation').setData(this.geojson);

        this.onLocationChanged(coords);
    }

    /**
     * Initialize event listeners needed to move the GeoJSON marker feature.
     *
     * Adds mouse events to:
     *  - Update the cursor when you hover over the marker
     *  - Add move listeners to update location whenever you press down on the marker.
     */
    initMovableMarker() {
        // Move the marker to wherever the user clicks on a map
        this.map.on('click', e => {
            if (this.clickTimeoutId) {
                // Another click happened within the timeout time, probably a double click to zoom.
                // Clear the existing timeout to prevent marker from moving.
                clearTimeout(this.clickTimeoutId);
                this.clickTimeoutId = undefined;
                return;
            }

            // Update the marker location after a small timeout to check if a double click to zoom occurs.
            this.clickTimeoutId = setTimeout(() => {
                this.clickTimeoutId = undefined;

                const coords = [e.lngLat.lng, e.lngLat.lat];
                this.geojson.features[0].geometry.coordinates = coords;
                this.map.getSource('installation').setData(this.geojson);
                this.onLocationChanged(coords);
            }, this.clickTimeout);
        });

        // Show move cursor when hovering over the marker
        this.map.on('mouseenter', 'installation', () => {
            this.canvas.style.cursor = 'move';
        });

        // Reset cursor when mouse leaves the marker
        this.map.on('mouseleave', 'installation', () => {
            this.canvas.style.cursor = '';
        });

        // Update cursor and set move listeners whenever the user grabs the marker with a mouse event.
        this.map.on('mousedown', 'installation', e => {
            // Prevent the default map drag behavior.
            e.preventDefault();

            this.canvas.style.cursor = 'grab';

            this.map.on('mousemove', this.onMove);
            this.map.once('mouseup', this.onUp);
        });

        // Update cursor and set move listeners whenever the user grabs the marker with a touch event.
        this.map.on('touchstart', 'installation', e => {
            if (e.points.length !== 1) return;

            // Prevent the default map drag behavior.
            e.preventDefault();

            this.map.on('touchmove', this.onMove);
            this.map.once('touchend', this.onUp);
        });
    }

    /**
     * Update marker position to follow the moving mouse.
     *
     * @param e
     */
    onMove(e) {
        const coords = e.lngLat;

        this.canvas.style.cursor = 'grabbing';

        this.geojson.features[0].geometry.coordinates = [coords.lng, coords.lat];
        this.map.getSource('installation').setData(this.geojson);
    }

    /**
     * Remove marker event listeners used for movement when the user lets go of the marker.
     *
     * @param e
     */
    // eslint-disable-next-line no-unused-vars
    onUp(e) {
        this.canvas.style.cursor = '';

        this.map.off('mousemove', this.onMove);
        this.map.off('touchmove', this.onMove);

        const coords = this.geojson.features[0].geometry.coordinates;
        this.onLocationChanged(coords);
    }

    /**
     * Emit a new location changed event with given coords
     *
     * @param coords - GeoJSON formatted array of coords: [ lng, lat ]
     */
    onLocationChanged(coords) {
        this.$emit('location-changed', {
            longitude: coords[0],
            latitude: coords[1],
        });
    }
}
</script>
